import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import Fuse from 'fuse.js';

// Search result interface
export interface SearchResult {
  id: string;
  slug: string;
  title: string;
  description: string;
  category: string;
  tags: string[];
  date: string;
  content: string;
  language: string;
}

// Search context interface
interface SearchContextType {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  searchResults: SearchResult[];
  isSearching: boolean;
  searchHistory: string[];
  performSearch: (query: string, language?: string) => void;
  clearSearch: () => void;
  addToHistory: (query: string) => void;
  clearHistory: () => void;
}

const SearchContext = createContext<SearchContextType | undefined>(undefined);

const SEARCH_HISTORY_KEY = 'searchHistory';
const MAX_HISTORY_ITEMS = 5;

// Fuse.js configuration for optimal search
const fuseOptions = {
  keys: [
    { name: 'title', weight: 3 }, // Title has highest priority
    { name: 'description', weight: 2 }, // Description is second
    { name: 'content', weight: 1 }, // Content has lowest priority
    { name: 'tags', weight: 1.5 },
    { name: 'category', weight: 1.5 },
  ],
  threshold: 0.4,
  distance: 100,
  minMatchCharLength: 2,
  includeScore: true,
  includeMatches: true,
};

export const SearchProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchHistory, setSearchHistory] = useState<string[]>([]);
  const [searchIndex, setSearchIndex] = useState<SearchResult[] | null>(null);
  const [fuse, setFuse] = useState<Fuse<SearchResult> | null>(null);

  // Load search index on mount
  useEffect(() => {
    const loadSearchIndex = async () => {
      try {
        const response = await fetch('/search-index.json');
        if (response.ok) {
          const data: SearchResult[] = await response.json();
          setSearchIndex(data);
          const fuseInstance = new Fuse(data, fuseOptions);
          setFuse(fuseInstance);
        }
      } catch (error) {
        console.error('Failed to load search index:', error);
      }
    };

    loadSearchIndex();
  }, []);

  // Load search history from localStorage
  useEffect(() => {
    if (typeof window !== 'undefined') {
      try {
        const stored = localStorage.getItem(SEARCH_HISTORY_KEY);
        if (stored) {
          const history = JSON.parse(stored);
          setSearchHistory(Array.isArray(history) ? history : []);
        }
      } catch (error) {
        console.error('Failed to load search history:', error);
      }
    }
  }, []);

  // Save search history to localStorage
  const saveHistoryToStorage = useCallback((history: string[]) => {
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem(SEARCH_HISTORY_KEY, JSON.stringify(history));
      } catch (error) {
        console.error('Failed to save search history:', error);
      }
    }
  }, []);

  // Add query to search history
  const addToHistory = useCallback(
    (query: string) => {
      if (!query.trim()) return;

      const trimmedQuery = query.trim();
      setSearchHistory(prevHistory => {
        // Remove if already exists
        const filtered = prevHistory.filter(item => item !== trimmedQuery);
        // Add to beginning
        const newHistory = [trimmedQuery, ...filtered].slice(0, MAX_HISTORY_ITEMS);
        saveHistoryToStorage(newHistory);
        return newHistory;
      });
    },
    [saveHistoryToStorage]
  );

  // Clear search history
  const clearHistory = useCallback(() => {
    setSearchHistory([]);
    if (typeof window !== 'undefined') {
      localStorage.removeItem(SEARCH_HISTORY_KEY);
    }
  }, []);

  // Perform search
  const performSearch = useCallback(
    (query: string, language?: string) => {
      const trimmedQuery = query.trim();

      if (!trimmedQuery || !fuse || !searchIndex) {
        setSearchResults([]);
        setSearchQuery('');
        return;
      }

      setIsSearching(true);
      setSearchQuery(trimmedQuery);

      try {
        // Perform Fuse.js search
        const results = fuse.search(trimmedQuery);

        // Extract results and filter by language if specified
        let filteredResults = results.map(result => result.item);
        
        if (language) {
          filteredResults = filteredResults.filter(item => item.language === language);
        }

        // Limit to 100 results
        const searchResults = filteredResults.slice(0, 100);

        setSearchResults(searchResults);
        addToHistory(trimmedQuery);
      } catch (error) {
        console.error('Search failed:', error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    },
    [fuse, searchIndex, addToHistory]
  );

  // Clear search
  const clearSearch = useCallback(() => {
    setSearchQuery('');
    setSearchResults([]);
  }, []);

  const value: SearchContextType = {
    searchQuery,
    setSearchQuery,
    searchResults,
    isSearching,
    searchHistory,
    performSearch,
    clearSearch,
    addToHistory,
    clearHistory,
  };

  return <SearchContext.Provider value={value}>{children}</SearchContext.Provider>;
};

// Custom hook to use search context
export const useSearch = (): SearchContextType => {
  const context = useContext(SearchContext);
  if (context === undefined) {
    throw new Error('useSearch must be used within a SearchProvider');
  }
  return context;
};
